import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProviders } from './contexts/AppContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OverviewDataProvider } from './contexts/OverviewDataContext';
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { Contact } from './pages/Contact';
import { Pricing } from './pages/Pricing';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsAndConditions } from './pages/TermsAndConditions';
import { Platform } from './pages/Platform';
import { Sidebar } from './components/SidebarResponsive';
import './mobile-responsive.css';

// ─── Lazy-loaded dashboard pages ────────────────────────────────────────────
// Each page (and its chart libraries) is only downloaded when the user
// navigates to that route, keeping the initial bundle small.
const Overview = React.lazy(() =>
  import(/* webpackChunkName: "page-overview" */ './pages/dashboard/Overview').then(m => ({ default: m.Overview }))
);
const Universe = React.lazy(() =>
  import(/* webpackChunkName: "page-universe" */ './pages/dashboard/Universe').then(m => ({ default: m.Universe }))
);
const Aggregations = React.lazy(() =>
  import(/* webpackChunkName: "page-aggregations" */ './pages/dashboard/Aggregations').then(m => ({ default: m.Aggregations }))
);
const PerformanceAttribution = React.lazy(() =>
  import(/* webpackChunkName: "page-performance" */ './pages/dashboard/PerformanceAttribution').then(m => ({ default: m.PerformanceAttribution }))
);
const AnalystDashboard = React.lazy(() =>
  import(/* webpackChunkName: "page-analyst" */ './pages/dashboard/AnalystDashboard').then(m => ({ default: m.AnalystDashboard }))
);

// Admin pages share one chunk — they're only reachable by admins
const AdminTeams = React.lazy(() =>
  import(/* webpackChunkName: "page-admin" */ './pages/dashboard/admin').then(m => ({ default: m.AdminTeams }))
);
const AdminUsers = React.lazy(() =>
  import(/* webpackChunkName: "page-admin" */ './pages/dashboard/admin').then(m => ({ default: m.AdminUsers }))
);
const AdminNotifications = React.lazy(() =>
  import(/* webpackChunkName: "page-admin" */ './pages/dashboard/admin').then(m => ({ default: m.AdminNotifications }))
);
const AdminData = React.lazy(() =>
  import(/* webpackChunkName: "page-admin" */ './pages/dashboard/admin').then(m => ({ default: m.AdminData }))
);
const MyData = React.lazy(() =>
  import(/* webpackChunkName: "page-admin" */ './pages/dashboard/admin').then(m => ({ default: m.MyData }))
);
const AdminLeads = React.lazy(() =>
  import(/* webpackChunkName: "page-admin" */ './pages/dashboard/admin').then(m => ({ default: m.AdminLeads }))
);
const AdminPlans = React.lazy(() =>
  import(/* webpackChunkName: "page-admin" */ './pages/dashboard/admin').then(m => ({ default: m.AdminPlans }))
);
const AdminAIInstructions = React.lazy(() =>
  import(/* webpackChunkName: "page-admin" */ './pages/dashboard/admin').then(m => ({ default: m.AdminAIInstructions }))
);
const AdminReviews = React.lazy(() =>
  import(/* webpackChunkName: "page-admin" */ './pages/dashboard/admin').then(m => ({ default: m.AdminReviews }))
);
const AdminDocs = React.lazy(() =>
  import(/* webpackChunkName: "page-admin" */ './pages/dashboard/admin/AdminDocs').then(m => ({ default: m.AdminDocs }))
);

// Loading spinner component
const LoadingScreen: React.FC = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#0a0f1c',
    color: '#fff'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(59, 130, 246, 0.3)',
        borderTop: '3px solid #3b82f6',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 16px'
      }} />
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      <p>Loading...</p>
    </div>
  </div>
);

// Protected route wrapper (admins → /dashboard/admin; analysts → /dashboard/analyst only; viewers → normal dashboard)
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === 'admin' || user?.role === 'ADMIN';
  const isAnalyst = user?.role === 'analyst' || user?.role === 'ANALYST';
  const isAdminRoute = location.pathname.startsWith('/dashboard/admin');
  const isAnalystRoute = location.pathname === '/dashboard/analyst';
  const isMyDataRoute = location.pathname === '/dashboard/my-data';
  const isDashboardRoute = location.pathname.startsWith('/dashboard');

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Admins: only /dashboard/admin/*
  if (isAdmin && isDashboardRoute && !isAdminRoute) {
    return <Navigate to="/dashboard/admin/teams" replace />;
  }

  // Analysts: analyst page + optional my-data (client uploads)
  if (isAnalyst && isDashboardRoute && !isAnalystRoute && !isMyDataRoute) {
    return <Navigate to="/dashboard/analyst" replace />;
  }

  // Viewers (and others): must not access analyst-only route
  if (!isAnalyst && isAnalystRoute) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <Sidebar />
      {children}
    </>
  );
};

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      
      {/* Public pages - accessible without authentication */}
      <Route path="/contact" element={<Contact />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/platform" element={<Platform />} />
      
      {/* Protected dashboard routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <OverviewDataProvider>
              <Overview />
            </OverviewDataProvider>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/universe" 
        element={
          <ProtectedRoute>
            <Universe />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/aggregations" 
        element={
          <ProtectedRoute>
            <Aggregations />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/performance" 
        element={
          <ProtectedRoute>
            <PerformanceAttribution />
          </ProtectedRoute>
        } 
      />
      <Route path="/dashboard/admin" element={<Navigate to="/dashboard/admin/teams" replace />} />
      <Route path="/dashboard/admin/teams" element={<ProtectedRoute><AdminTeams /></ProtectedRoute>} />
      <Route path="/dashboard/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
      <Route path="/dashboard/admin/notifications" element={<ProtectedRoute><AdminNotifications /></ProtectedRoute>} />
      <Route path="/dashboard/admin/data" element={<ProtectedRoute><AdminData /></ProtectedRoute>} />
      <Route path="/dashboard/my-data" element={<ProtectedRoute><MyData /></ProtectedRoute>} />
      <Route path="/dashboard/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
      <Route path="/dashboard/admin/plans" element={<ProtectedRoute><AdminPlans /></ProtectedRoute>} />
      <Route path="/dashboard/admin/ai" element={<ProtectedRoute><AdminAIInstructions /></ProtectedRoute>} />
      <Route path="/dashboard/admin/reviews" element={<ProtectedRoute><AdminReviews /></ProtectedRoute>} />
      <Route path="/dashboard/admin/docs" element={<ProtectedRoute><AdminDocs /></ProtectedRoute>} />
      <Route 
        path="/dashboard/analyst" 
        element={
          <ProtectedRoute>
            <AnalystDashboard />
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <Router>
      <AuthProvider>
        <AppProviders>
          <AppRoutes />
        </AppProviders>
      </AuthProvider>
    </Router>
  );
}
